/**
 * Artboard 2 · Primer uso — the empty state of Gastos, rendered by Viajes when the employee has no reports at all, and
 * (3 Sep 2026) the first-use card: three legal entities, one app — the employee picks the company that pays their salary
 * before anything else. `EntityChooser` is shared with CodigoFiscal, where the choice can be changed.
 */
import { useState } from 'react';
import { ApiError, call } from '../api/client.ts';
import type { EntityOption } from '../api/types.ts';
import { useLoad } from '../state/hooks.ts';
import { AppHeader, Button, ErrorNote, Icon, Loading } from '../theme/ui.tsx';
import { useProfile } from './Perfil.tsx';
import { InstallCard } from './Instalar.tsx';
import type { Session } from '../App.tsx';

/** The three entities as a radio list; `Guardar` PATCHes /me and reports the chosen one. */
export function EntityChooser({ current, onChosen, cta = 'Continuar' }: { current: string | null; onChosen: (e: EntityOption) => void; cta?: string }) {
  const list = useLoad(() => call('entities.list'), []);
  const [picked, setPicked] = useState<string | null>(current);
  const [busy, setBusy] = useState(false); const [error, setError] = useState<string | null>(null);
  if (list.loading) return <Loading />;
  if (list.error || !list.data) return <ErrorNote error={list.error ?? new Error('Sin datos')} retry={list.reload} />;
  const entities = list.data.entities;
  const save = async () => {
    const e = entities.find((x) => x.entityId === picked); if (!e) return;
    setBusy(true); setError(null);
    try { await call('me.patch', { body: { entityId: e.entityId } }); onChosen(e); }
    catch (err) { setError(err instanceof ApiError ? err.message : 'No se pudo guardar.'); setBusy(false); }
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }} role="radiogroup" aria-label="Empresa">
      {entities.map((e) => {
        const on = e.entityId === picked;
        return (
          <button key={e.entityId} type="button" role="radio" aria-checked={on} onClick={() => setPicked(e.entityId)} className="card card--pad"
            style={{ display: 'flex', alignItems: 'center', gap: 14, textAlign: 'left', width: '100%', cursor: 'pointer', borderColor: on ? 'var(--ink)' : undefined, borderWidth: on ? 1.5 : 1 }}>
            <span aria-hidden="true" style={{ width: 20, height: 20, borderRadius: '50%', border: `${on ? 6 : 1.5}px solid ${on ? 'var(--ink)' : 'var(--line-3)'}`, boxSizing: 'border-box', flexShrink: 0, background: 'var(--bg-card)' }} />
            <span className="grow" style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <span className="h4">{e.name}</span>
              <span className="mono sub sub--sm">{e.rfc}</span>
            </span>
          </button>
        );
      })}
      {error && <div style={{ fontSize: 13, color: 'var(--red)' }}>{error}</div>}
      <Button onClick={save} busy={busy} disabled={!picked} style={{ marginTop: 6 }}>{cta}</Button>
    </div>
  );
}

export default function Inicio({ session, needsEntity, onEntityChosen }: { session: Session; needsEntity?: boolean; onEntityChosen?: () => void }) {
  const name = session.displayName;
  const [profile, openProfile] = useProfile(session);
  if (needsEntity) {
    return (
      <div className="screen">
        <div className="phone">
          <AppHeader name={name} onProfile={openProfile} />
          <div className="pad" style={{ display: 'flex', flexDirection: 'column', gap: 18, paddingTop: 10 }}>
            <div>
              <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.018em', lineHeight: 1.25 }}>¿En qué empresa estás?</div>
              <div className="lead" style={{ marginTop: 9 }}>Elige la que paga tu nómina. Las facturas de tus gastos saldrán a su nombre. Si cambias de empresa, lo ajustas después desde <b>Datos de facturación</b>.</div>
            </div>
            <EntityChooser current={null} onChosen={() => onEntityChosen?.()} />
          </div>
        </div>
        {profile}
      </div>
    );
  }
  return (
    <div className="screen">
      <div className="phone">
        <AppHeader name={name} onProfile={openProfile} />
        <InstallCard />
        <div className="grow" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '0 42px', gap: 22 }}>
          <Icon.Folder size={76} color="var(--ink-5)" width={1.6} />
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.018em', lineHeight: 1.25 }}>Aún no tienes reportes</div>
            <div className="lead" style={{ marginTop: 11 }}>Crea el reporte primero — un viaje, un mes, lo que sea. Todos los comprobantes van dentro de un reporte, así ninguno se queda suelto.</div>
          </div>
        </div>
        <div className="footer"><Button to="/nuevo" icon={<Icon.Plus size={19} />}>Nuevo reporte</Button></div>
      </div>
      {profile}
    </div>
  );
}
